import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { SluggedParams } from "../../../types/params";
import { Article as ArticleComponent } from "../../../components/article";

import { Previewable } from "../../../types/previewable";
import { revalidationInterval } from "../../../utils/revalidationInterval";
import { QueryableSanityData } from "../../../types/sanity";
import {
  getInsightArticle,
  getInsightArticleQuery,
  getInsightArticleSlugs,
  InsightArticleData,
} from "../../../services/getInsightArticle";
import LiveQuery from "@sanity/preview-kit/live-query";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import Block from "@/components/block";
import { Breadcrumbs } from "@/components/breadcrumbs";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import { getClient } from "lib/sanity.server";
import SEOPageHead from "@/components/SEOPageHead";
import FooterModular from "@/components/FooterModular";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";

type InsightArticleProps = Previewable<{
  payload: QueryableSanityData<InsightArticleData>;
  navPayload: HeaderNavigationProps;
  footerPayload: FooterNavigationProps;
}>;

const token = process.env.SANITY_API_TOKEN;

const InsightArticlePage: NextPage<InsightArticleProps> = (props) => {
  const articleData = props.payload.data;

  if (articleData === null) {
    return null;
  }

  const seo = articleData.seo || { title: articleData.title };

  const breadcrumbs = [
    { name: "Learning", path: "/learning" },
    { name: "Adoption Blog", path: "/learning/adoption-blog" },
    {
      name: articleData.title ?? "Article",
      path: `/learning/adoption-blog/${articleData.slug?.current}`,
    },
  ];

  return (
    <LiveQuery
      enabled={props.preview}
      query={getInsightArticleQuery({ preview: props.preview })}
      initialData={articleData}
      params={{ slug: articleData.slug }}
    >
      <>
        {seo && <SEOPageHead seo={seo} />}
        <HeaderNavigation navSections={props.navPayload.navSections} />
        <Block flushBottom flushTop className="mt-20 pt-5">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </Block>
        <InsightArticle {...articleData} />
        <FooterModular footerData={props.footerPayload} />
      </>
    </LiveQuery>
  );
};

const InsightArticle = (props: InsightArticleData) => {
  return (
    <ArticleComponent
      article={props}
      suggestedArticles={props.suggestedArticles}
    />
  );
};

export const getStaticPaths: GetStaticPaths<SluggedParams> = async () => {
  const slugsResult = await getInsightArticleSlugs();
  // TODO retry on error
  const paths = slugsResult.ok ? slugsResult.content : [];

  return {
    // only paths which map to real article slugs are considered valid; we
    // don't bother trying to getStaticProps if the path is invalid
    paths: paths.map((slug: string) => ({ params: { slug } })),
    // instruct getStaticProps to run before build - used for redirect to notFound if necessary
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<
  InsightArticleProps,
  SluggedParams
> = async (context) => {
  const slug = context.params?.slug ?? "";
  const preview: boolean = !!context.draftMode;

  const insightArticleResult = await getInsightArticle({
    preview,
    slug,
  });

  if (!insightArticleResult.ok) {
    return {
      notFound: true,
    };
  }

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerData: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  return {
    props: {
      payload: insightArticleResult.content,
      navPayload: navPayload,
      footerPayload: footerData,
      preview,
      token: preview ? token : "",
    },
    revalidate: revalidationInterval,
  };
};

export default InsightArticlePage;
